import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  KIND, STATUS_TONE, titleCase, fmtMoney, fmtNum, fmtDate, exportExcel, exportPdf, apiError,
} from "@/lib/partnerApi";

export function StatusBadge({ value, tid }) {
  if (!value) return <span className="text-slate-400">—</span>;
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_TONE[value] || "bg-slate-100 text-slate-600 border-slate-200"}`}
      data-testid={tid}
    >
      {titleCase(value)}
    </span>
  );
}

const SUB_NAV = {
  supplier: [
    { to: "", label: "Directory", end: true },
    { to: "/dashboard", label: "Dashboard" },
    { to: "/approvals", label: "Approvals" },
    { to: "/alerts", label: "Alerts" },
    { to: "/rfqs", label: "RFQ & Quotations" },
    { to: "/procurement-plan", label: "Procurement Plan" },
    { to: "/reports", label: "Reports" },
  ],
  dealer: [
    { to: "", label: "Directory", end: true },
    { to: "/dashboard", label: "Dashboard" },
    { to: "/approvals", label: "Approvals" },
    { to: "/alerts", label: "Alerts" },
    { to: "/pricing", label: "Price Lists" },
    { to: "/reports", label: "Reports" },
  ],
};

/** Page frame shared by every Supplier / Dealer Management screen. */
export function ModuleShell({ kind, title, subtitle, actions, children, tid }) {
  const { user } = useAuth();
  const k = KIND[kind];
  const items = [...SUB_NAV[kind]];
  if (user?.role === "admin") items.push({ to: "/settings", label: "Settings" });
  return (
    <div className="space-y-5" data-testid={tid}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{k.eyebrow} · {k.label} Management</div>
          <h1 className="text-3xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
      <nav className="flex gap-1 border-b border-slate-200 overflow-x-auto scroll-thin" data-testid={`${kind}-subnav`}>
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={`${k.base}${n.to}`}
            end={n.end}
            className={({ isActive }) =>
              `px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${isActive ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
      {children}
    </div>
  );
}

export function Kpi({ label, value, sub, tone = "text-slate-900", tid, onClick }) {
  return (
    <div className={`kpi-card ${onClick ? "cursor-pointer" : ""}`} data-testid={tid} onClick={onClick}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={`mt-1.5 text-2xl font-extrabold ${tone}`} style={{ fontFamily: "Manrope" }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export function Field({ label, required, error, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <div className="mt-1">{children}</div>
      {error && <div className="text-[11px] text-red-600 mt-0.5">{error}</div>}
    </div>
  );
}

export function KV({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={`text-sm text-slate-800 [overflow-wrap:anywhere] ${mono ? "mono" : ""}`}>{value === undefined || value === null || value === "" ? "—" : value}</div>
    </div>
  );
}

export function renderCell(col, v, row) {
  if (col.type === "money") return fmtMoney(v, row?.currency || "INR");
  if (col.type === "number") return fmtNum(v);
  if (col.type === "date") return fmtDate(v);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (["master_status", "approval_status", "status", "state", "result", "severity"].includes(col.key)) return <StatusBadge value={v} />;
  if (v === null || v === undefined || v === "") return "—";
  return typeof v === "string" ? v.replace(/_/g, " ") : String(v);
}

/** Generic table over {columns:[{key,label,type}], rows}. */
export function DataTable({ columns, rows, empty = "No records.", onRowClick, tid, maxHeight }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-auto scroll-thin" style={maxHeight ? { maxHeight } : undefined}>
      <table className="w-full text-sm" data-testid={tid}>
        <thead className="sticky top-0 z-10">
          <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500 bg-slate-50">
            {columns.map((c) => (
              <th key={c.key} className={`py-2 px-3 whitespace-nowrap ${c.type === "money" || c.type === "number" ? "text-right" : ""}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="py-8 text-center text-sm text-slate-500">{empty}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              className={`border-t border-slate-100 ${onRowClick ? "cursor-pointer hover:bg-blue-50/40" : "hover:bg-slate-50/60"}`}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className={`py-2 px-3 text-xs text-slate-700 ${c.type === "money" || c.type === "number" ? "text-right mono" : ""}`}>
                  {c.render ? c.render(r) : renderCell(c, r[c.key], r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ExportButtons({ kind, title, columns, rows, subtitle, disabled }) {
  const run = async (fn) => {
    try {
      await fn(kind, title, columns.filter((c) => !c.render || c.exportable !== false), rows, subtitle);
    } catch (e) {
      toast.error(apiError(e, "Export failed"));
    }
  };
  return (
    <>
      <Button variant="outline" size="sm" disabled={disabled || !rows.length} onClick={() => run(exportExcel)} data-testid="export-excel-btn">
        <Download className="w-4 h-4 mr-1.5" /> Excel
      </Button>
      <Button variant="outline" size="sm" disabled={disabled || !rows.length} onClick={() => run(exportPdf)} data-testid="export-pdf-btn">
        <FileText className="w-4 h-4 mr-1.5" /> PDF
      </Button>
    </>
  );
}
